import { Button, Grid, IconButton, Typography } from '@material-ui/core';
import Chip from 'components/Chip';
import TableListView from 'components/TableListView';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import TerminalCrudPopup from './TerminalCrudPopup';
import ConfirmModal from 'Views/Common/ConfirmModal';
import styles from './TerminalList.module.scss';
import { formatDate } from 'utils';
import {
  createTerminalForLocation,
  deleteTerminalForLocation,
  getTerminalListForLocation,
  updateTerminalForLocation,
} from 'Services/terminal.service';
import Utils from 'Shared/Utils';
import { ADD_NOTIFICATION } from 'context/actions';
import { Context } from 'context';
import { ReactComponent as EmptyTerminalList } from 'assets/images/terminal-list-empty.svg';
import PropTypes from 'prop-types';

const TerminalList = ({ locationId }) => {
  const { dispatch } = useContext(Context);

  const [loading, setLoading] = useState(true);

  const [operationLoading, setOperationLoading] = useState(false);

  const [terminalList, setTerminalList] = useState([]);

  const initPopupData = useMemo(
    () => ({
      open: false,
      mode: 'ADD',
      data: null,
    }),
    [],
  );

  const initDeletePopupData = useMemo(
    () => ({
      open: false,
      rowIndex: null,
      loading: false,
    }),
    [],
  );

  // Popup config
  const [popupConfig, setPopupConfig] = useState(initPopupData);

  // Delete confirm dialog :
  const [deleteConfirm, setDeleteConfirm] = useState(initDeletePopupData);

  const setGlobalNotification = (severity, message) =>
    dispatch({
      type: ADD_NOTIFICATION,
      payload: {
        notification: {
          severity,
          message,
        },
      },
    });

  // Terminal Data Mapper
  const terminalDataMapper = (
    { terminal_display_name, id, created_at, status },
    index,
  ) => ({
    terminalName: terminal_display_name ?? '',
    status,
    addedOn: created_at,
    id: id,
    index,
  });

  // Fetch the terminal list :
  const fetchTerminalList = async () => {
    const terminalListResponse = await getTerminalListForLocation(locationId);
    if (
      Utils.checkIfSuccess(terminalListResponse) &&
      terminalListResponse?.data
    ) {
      const terminalList = terminalListResponse?.data?.map(terminalDataMapper);
      setTerminalList(terminalList);
      setLoading(false);
    } else {
      setTerminalList([]);
      setGlobalNotification(
        'error',
        terminalListResponse?.message ?? 'Failed to fetch the terminals',
      );
    }
  };

  useEffect(() => {
    fetchTerminalList();
  }, []);

  // return empty element for loading :
  if (loading) return <></>;

  const handleEditBtn = (rowIndex) => {
    setPopupConfig({
      open: true,
      mode: 'EDIT',
      data: terminalList[rowIndex],
    });
  };

  const handleAddBtn = () => {
    setPopupConfig({
      open: true,
      mode: 'ADD',
      data: null,
    });
  };

  const handleDelete = (rowIndex) => {
    setDeleteConfirm({
      open: true,
      rowIndex: rowIndex,
      loading: false,
    });
  };

  const handleCrudConfirm = async (data) => {
    setOperationLoading(true);
    if (popupConfig?.mode === 'ADD') {
      const terminalAddResponse = await createTerminalForLocation(
        locationId,
        data,
      );
      if (
        Utils.checkIfSuccess(terminalAddResponse) &&
        terminalAddResponse?.data
      ) {
        setGlobalNotification('success', 'Terminal created successfully');
        setPopupConfig(initPopupData);
        setTerminalList((prevTerminalList) => {
          const newTerminalList = [
            ...prevTerminalList,
            terminalDataMapper(
              terminalAddResponse?.data,
              prevTerminalList.length,
            ),
          ];
          return newTerminalList;
        });
      } else {
        setGlobalNotification(
          'error',
          terminalAddResponse?.message ?? 'Failed to create the terminal',
        );
      }
    } else {
      const terminalUpdateResponse = await updateTerminalForLocation(
        locationId,
        data,
      );
      if (
        Utils.checkIfSuccess(terminalUpdateResponse) &&
        terminalUpdateResponse?.data
      ) {
        setGlobalNotification('success', 'Terminal updated successfully');
        setPopupConfig(initPopupData);
        setTerminalList((prevTerminalList) => {
          const newTerminalList = [...prevTerminalList];
          newTerminalList[data.index] = terminalDataMapper(
            terminalUpdateResponse?.data,
            data.index,
          );
          return newTerminalList;
        });
      } else {
        setGlobalNotification(
          'error',
          terminalUpdateResponse?.message ?? 'Failed to update the terminal',
        );
      }
    }
    setOperationLoading(false);
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirm((prev) => ({
      ...prev,
      loading: true,
    }));
    const { id } = terminalList[deleteConfirm?.rowIndex];
    const terminalDeleteResponse = await deleteTerminalForLocation(id);
    if (Utils.checkIfSuccess(terminalDeleteResponse)) {
      setDeleteConfirm(initDeletePopupData);
      setTerminalList((prevTerminalList) => {
        const newTerminalList = [...prevTerminalList];
        newTerminalList.splice(deleteConfirm?.rowIndex, 1);
        return newTerminalList;
      });
      setGlobalNotification('success', 'Terminal deleted successfully');
    } else {
      setGlobalNotification(
        'error',
        terminalDeleteResponse?.message ?? 'Failed to delete the terminal',
      );
      setDeleteConfirm((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  // Column Data :
  const tableColumns = [
    {
      name: 'terminalName',
      label: 'Terminal Name',
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: (statusData) => {
          return <Chip label={statusData} status={statusData} />;
        },
      },
    },
    {
      name: 'addedOn',
      label: 'Added On',
      //formatDate,
      options: {
        customBodyRender: (addedOn) => {
          return formatDate(addedOn);
        },
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        customBodyRender: (actionData, { rowIndex }) => {
          return (
            <Grid container>
              <IconButton
                onClick={() => handleEditBtn(rowIndex)}
                size="small"
                className="pr-16"
              >
                <CreateIcon className="theme_color_orange" fontSize="inherit" />
              </IconButton>
              <IconButton onClick={() => handleDelete(rowIndex)} size="small">
                <DeleteIcon className="theme_color_orange" fontSize="inherit" />
              </IconButton>
            </Grid>
          );
        },
      },
    },
  ];

  const renderAddTerminalButton = () => {
    return (
      <Button
        onClick={() => handleAddBtn()}
        color="secondary"
        size="large"
        data-testid="terminal-add-button"
      >
        Add Terminal
      </Button>
    );
  };

  const renderEmptyContent = () => {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        direction="column"
      >
        <Grid item>
          <EmptyTerminalList />
        </Grid>
        <Grid item>
          <Typography className={styles.noTerminalMsgHeader}>
            No Terminals Added
          </Typography>
        </Grid>
        <Grid item>
          <Typography className={styles.noTerminalSubText}>
            You haven’t added any payment terminals yet
          </Typography>
        </Grid>
        <Grid item>{renderAddTerminalButton()}</Grid>
      </Grid>
    );
  };

  return (
    <>
      <TableListView
        headerTitle="Terminal Management"
        headerSection={true}
        tableData={terminalList}
        tableOptions={{
          pagination: false,
        }}
        tableColumns={tableColumns}
        emptyPlaceHolderDom={renderEmptyContent()}
        otherButtons={renderAddTerminalButton()}
      />

      {popupConfig?.open ? (
        <TerminalCrudPopup
          data={popupConfig?.data}
          open={true}
          onConfirm={handleCrudConfirm}
          onClose={() => setPopupConfig(initPopupData)}
          mode={popupConfig?.mode}
          isLoading={operationLoading}
        />
      ) : (
        <></>
      )}

      {deleteConfirm?.open ? (
        <ConfirmModal
          isOpen={deleteConfirm?.open}
          closeModal={() => setDeleteConfirm(initDeletePopupData)}
          emitConfirmEvent={handleConfirmDelete}
          isLoading={deleteConfirm?.loading}
          options={{
            title: `Delete Terminal`,
            description: <>Are you sure you want to delete this terminal?</>,
            confirmText: 'Yes, Sure',
            cancelText: 'Cancel',
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};

TerminalList.propTypes = {
  locationId: PropTypes.string,
};

export default TerminalList;
